/**
 * F2 — Schedule ENGINE (pure, agnostic of block types).
 *
 * Stable order, accumulated timeline (each block starts where the previous
 * ends, anchored to the meeting time) and swap-based reordering with dense
 * renumbering. The engine does not know the concrete types (kinds.ts):
 * only titles, durations and order — the LEARNING_BLOCKS pattern from `aula`.
 */

export interface ScheduleBlockLike {
  id: string;
  kind?: string | null;
  title?: string | null;
  duration_minutes: number | null;
  sort_order: number | null;
  created_at?: string | null;
}

/** Stable order of the program: sort_order and, on ties, creation. */
export function orderScheduleBlocks<T extends ScheduleBlockLike>(rows: readonly T[]): T[] {
  return rows.toSorted((a, b) => {
    const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (byOrder !== 0) {
      return byOrder;
    }
    return (a.created_at ?? "").localeCompare(b.created_at ?? "");
  });
}

/** Sanitized duration of a block (negatives/absent count as 0). */
function durationOf(block: ScheduleBlockLike): number {
  return Math.max(0, block.duration_minutes ?? 0);
}

export interface TimelineEntry<T extends ScheduleBlockLike> {
  block: T;
  /** Minutes from meeting start until the block STARTS. */
  offsetMinutes: number;
  /** Effective (sanitized) duration of the block. */
  durationMinutes: number;
  /** Start/end instant (ms epoch) if anchored to a meeting time. */
  startMs: number | null;
  endMs: number | null;
}

/**
 * Accumulated timeline of the program: each block starts where the previous
 * ends. `meetingStartMs = null` produces relative offsets (no clock).
 * The INPUT must already be ordered (orderScheduleBlocks).
 */
export function computeTimeline<T extends ScheduleBlockLike>(
  orderedBlocks: readonly T[],
  meetingStartMs: number | null,
): TimelineEntry<T>[] {
  const entries: TimelineEntry<T>[] = [];
  let offset = 0;
  for (const block of orderedBlocks) {
    const duration = durationOf(block);
    entries.push({
      block,
      offsetMinutes: offset,
      durationMinutes: duration,
      startMs: meetingStartMs === null ? null : meetingStartMs + offset * 60_000,
      endMs: meetingStartMs === null ? null : meetingStartMs + (offset + duration) * 60_000,
    });
    offset += duration;
  }
  return entries;
}

/** Total duration of the program in minutes. */
export function totalDurationMinutes(blocks: readonly ScheduleBlockLike[]): number {
  return blocks.reduce((total, block) => total + durationOf(block), 0);
}

export interface OrderAssignment {
  id: string;
  sortOrder: number;
}

/**
 * Moves a block one position up/down and returns the DENSE (1-based)
 * renumbering of the WHOLE program — the UI only persists rows whose
 * `sort_order` changed. `null` if the move is a no-op (ends or unknown id).
 * The input must already be ordered.
 */
export function moveBlock(
  orderedBlocks: readonly ScheduleBlockLike[],
  id: string,
  direction: "up" | "down",
): OrderAssignment[] | null {
  const index = orderedBlocks.findIndex((block) => block.id === id);
  if (index === -1) {
    return null;
  }
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= orderedBlocks.length) {
    return null;
  }
  const ids = orderedBlocks.map((block) => block.id);
  [ids[index], ids[target]] = [ids[target] ?? "", ids[index] ?? ""];
  return ids.map((blockId, position) => ({
    id: blockId,
    sortOrder: position + 1,
  }));
}

/**
 * Minimal changes to persist after a renumbering: only the rows whose
 * sort_order differs from the current one.
 */
export function changedAssignments(
  orderedBlocks: readonly ScheduleBlockLike[],
  assignments: readonly OrderAssignment[],
): OrderAssignment[] {
  const current = new Map(orderedBlocks.map((block) => [block.id, block.sort_order ?? 0]));
  return assignments.filter((a) => current.get(a.id) !== a.sortOrder);
}
